// Cookie consent storage + helpers (GDPR/ePrivacy friendly)
// We don't currently load any tracking — this banner is forward-compatible
// so analytics/marketing scripts can check `getConsent().analytics` before init.

export type ConsentCategories = {
  necessary: true; // always true, locked
  preferences: boolean;
  analytics: boolean;
  marketing: boolean;
};

export type StoredConsent = ConsentCategories & {
  timestamp: number;
  version: number;
};

const STORAGE_KEY = "sida_cookie_consent_v1";
const VERSION = 1;
export const CONSENT_EVENT = "sida:cookie-consent-changed";

export const DEFAULT_CONSENT: ConsentCategories = {
  necessary: true,
  preferences: false,
  analytics: false,
  marketing: false,
};

export function getConsent(): StoredConsent | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredConsent;
    if (parsed.version !== VERSION) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function saveConsent(categories: Omit<ConsentCategories, "necessary"> & { necessary?: true }) {
  const value: StoredConsent = {
    necessary: true,
    preferences: !!categories.preferences,
    analytics: !!categories.analytics,
    marketing: !!categories.marketing,
    timestamp: Date.now(),
    version: VERSION,
  };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
    window.dispatchEvent(new CustomEvent(CONSENT_EVENT, { detail: value }));
  } catch {
    /* ignore quota errors */
  }
}

export function clearConsent() {
  try {
    localStorage.removeItem(STORAGE_KEY);
    window.dispatchEvent(new CustomEvent(CONSENT_EVENT, { detail: null }));
  } catch {
    /* ignore */
  }
}

// Public API to re-open the banner from anywhere (e.g. footer link)
export const OPEN_BANNER_EVENT = "sida:cookie-banner-open";
export function openCookieSettings() {
  window.dispatchEvent(new CustomEvent(OPEN_BANNER_EVENT));
}
